import {DOM} from '@levcharity/resources/scripts/helpers/DOM';
import {Helper} from '@levcharity/resources/scripts/helpers/Helper';

export class MyAccountParticipantDashboard {
    #dashboardData = {};
    #dashboard;

    constructor() {
        this.#dashboard = DOM.el('.w4f-dashboard-container');
        if (!this.#dashboard) {
            return;
        }

        this.#initProgressBars();
        this.#initFundraisingSummaryProgress();
        this.#fetchData();
        this.#fetchFundraisingSummary();
        this.#addEvents();
    }

    #initProgressBars(percentage = null) {
        const progressEls = DOM.els(
            '.profile-progress-bar-wrapper .campaign_progressbar .levcharity_progressbar',
            this.#dashboard
        );

        progressEls.forEach((progress) => {
            const value =
                percentage !== null && percentage !== undefined
                    ? percentage
                    : progress.dataset.percentage ?? '0';

            const width = `${value}%`;
            progress.style.setProperty('--width', width);
            progress.style.width = width;
            progress.style.setProperty('--opacity', '1');
            progress.removeAttribute('data-percentage');
        });
    }

    #initFundraisingSummaryProgress() {
        const summary = DOM.el('.w4f-dashboard-fundraising-summary', this.#dashboard);
        if (!summary) {
            return;
        }

        const progressbar = DOM.el('.progress-bar-wrapper', summary);
        if (!progressbar) {
            return;
        }

        const progress =
            progressbar.style.getPropertyValue('--progress')?.trim() ||
            window.getComputedStyle(progressbar).getPropertyValue('--progress')?.trim() ||
            '0';

        this.#setCircularProgress(progressbar, progress);
    }

    #setCircularProgress(progressbar, percentage) {
        if (!progressbar) {
            return;
        }

        const pct = Math.min(100, Math.max(0, Number(percentage) || 0));
        progressbar.style.setProperty('--progress', String(pct));
    }

    async #fetchData() {
        const {Helper: RequestHelper} = await import(
            '@levcharity/resources/scripts/helpers/Helper.js' /* webpackChunkName: "levcharity-helper" */
        );

        RequestHelper.request(admin_ajax[0], {
            action: this.#dashboard?.dataset.action,
            token: this.#dashboard?.dataset.token,
        }).then((response) => {
            if (response.success) {
                this.#dashboardData = {
                    participantData: response.participant_data,
                    levelData: response.level_data,
                };
                this.#renderProfile();
            }
        });
    }

    async #fetchFundraisingSummary() {
        const summary = DOM.el('.w4f-dashboard-fundraising-summary', this.#dashboard);

        if (!summary?.dataset?.action) {
            return;
        }

        const {Helper: RequestHelper} = await import(
            '@levcharity/resources/scripts/helpers/Helper.js' /* webpackChunkName: "levcharity-helper" */
        );

        RequestHelper.request(admin_ajax[0], {
            action: summary.dataset.action,
            token: summary.dataset.token,
            timeline: '1y',
        }).then((response) => {
            if (response.success) {
                this.#renderFundraisingSummary(summary, response);
            }
        });
    }

    #renderProfile() {
        const participantData = this.#dashboardData.participantData;
        const levelData = this.#dashboardData.levelData;
        const profileTotalsWrapper = DOM.el('.profile-totals-wrapper');
        const totalDonationCount = DOM.el('.count', DOM.el('.total-donations .count-wrapper', profileTotalsWrapper));
        const totalDonorsCount = DOM.el('.count', DOM.el('.total-donors .count-wrapper', profileTotalsWrapper));
        const currencySymbol = Helper.resolveCurrencySymbol(this.#dashboard.dataset.currencySymbol);

        this.#numberAnimation(participantData.total_amount, totalDonationCount, currencySymbol, '', 0, 'auto');
        this.#numberAnimation(levelData.donors_count, totalDonorsCount, '', '', 0, false);

        const progressBar = DOM.el('.profile-progress-bar-wrapper');
        const countWrapper = DOM.el('.count-wrapper', progressBar);
        const nextBadgeCountWrapper = DOM.el('.next-badge-count-wrapper', progressBar);
        const needToNextLevelMobile = DOM.el('.need-to-next-level-mobile', progressBar);
        const progressTarget =
            levelData.maxAmountRange || levelData.userNextLevel?.[0]?.amountRange?.min || 0;

        this.#numberAnimation(
            levelData.userNeedToNextLevel,
            DOM.el('.count', nextBadgeCountWrapper),
            currencySymbol,
            '',
            0,
            false
        );
        this.#numberAnimation(levelData.userTotalRaised, DOM.el('.current-count', countWrapper), currencySymbol, '', 0, 'auto');
        this.#numberAnimation(progressTarget, DOM.el('.max-count', countWrapper), currencySymbol, '', 0, false);
        if (needToNextLevelMobile) {
            this.#numberAnimation(levelData.userNeedToNextLevel, needToNextLevelMobile, currencySymbol, '', 0, false);
        }

        const raised = Number(levelData.userTotalRaised) || 0;
        const target = Number(progressTarget) || 0;
        const progressPercentage = target > 0 ? Math.min((raised / target) * 100, 100) : 0;

        this.#initProgressBars(progressPercentage);

        DOM.els('.profile-bucks-wrapper', this.#dashboard).forEach((profileBucksWrapper) => {
            this.#numberAnimation(levelData.userCoin, DOM.el('.count', profileBucksWrapper), '', '', 0, false);
        });
    }

    #renderFundraisingSummary(summary, data) {
        const currencySymbol = Helper.resolveCurrencySymbol(
            summary.dataset.currencySymbol,
            this.#dashboard.dataset.currencySymbol
        );
        const progressbar = DOM.el('.progress-bar-wrapper', summary);
        const totalsToday = DOM.el('.totals_total', summary);
        const raised = DOM.el('.total_raised', summary);
        const goal = DOM.el('.goal', summary);
        const percentageEl = DOM.el('.circular-progress-percentage', summary);
        const goalValue = Number(data.goal) || 0;
        const raisedValue = Number(data.total_raised) || 0;
        const percentage = goalValue > 0 ? Math.min(100, raisedValue / (goalValue / 100)) : 0;

        this.#setCircularProgress(progressbar, percentage);

        const todayText = this.#formatAnimatedAmount(data.total_today, currencySymbol);
        const raisedText = this.#formatAnimatedAmount(data.total_raised, currencySymbol, 'auto');
        const goalText = this.#formatAnimatedAmount(data.goal, currencySymbol, false);

        this.#lockAnimatedTextWidth(totalsToday, todayText);
        this.#lockAnimatedTextWidth(raised, raisedText);
        this.#lockAnimatedTextWidth(goal, goalText);
        this.#lockAnimatedTextWidth(percentageEl, `${Math.round(percentage)}%`);

        this.#numberAnimation(data.total_today, totalsToday, currencySymbol, '', 0, 'auto');
        this.#numberAnimation(data.total_raised, raised, currencySymbol, '', 0, 'auto');
        this.#numberAnimation(data.goal, goal, currencySymbol, '', 0, false);
        this.#numberAnimation(percentage, percentageEl, '', '%', 0, false);
    }

    #formatAnimatedAmount(value, currencySymbol, withFractions = true) {
        const num = Number(value || 0);
        const decimals = this.#amountFractionDigits(num, withFractions);

        return currencySymbol + num.toFixed(decimals);
    }

    #amountFractionDigits(num, withFractions) {
        if (withFractions === false) {
            return 0;
        }

        if (withFractions === 'auto') {
            return Math.floor(num) === num ? 0 : 2;
        }

        return 2;
    }

    #lockAnimatedTextWidth(el, text) {
        if (!el || text === undefined || text === null) {
            return;
        }

        el.style.display = 'inline-block';
        el.style.whiteSpace = 'nowrap';
        el.style.minWidth = `${this.#measureTextWidth(el, String(text))}px`;
    }

    #measureTextWidth(el, text) {
        const probe = document.createElement('span');
        const style = window.getComputedStyle(el);

        probe.textContent = text;
        probe.style.cssText = [
            'position:absolute',
            'visibility:hidden',
            'white-space:nowrap',
            'pointer-events:none',
            `font:${style.font}`,
            `font-size:${style.fontSize}`,
            `font-weight:${style.fontWeight}`,
            `font-family:${style.fontFamily}`,
            `letter-spacing:${style.letterSpacing}`,
        ].join(';');

        document.body.appendChild(probe);
        const width = probe.offsetWidth;
        probe.remove();

        return width;
    }

    async #numberAnimation(to, el, prefix, suffix = '', from = 0, withFractions = true) {
        if (!el) {
            return;
        }

        const {gsap} = await import('gsap' /* webpackChunkName: "gsap" */);
        const tweenObj = {value: Number(from) || 0};
        const targetValue = Number(to) || 0;
        const fractionMode = withFractions;

        gsap.to(tweenObj, {
            value: targetValue,
            duration: 0.8,
            ease: 'power2.inOut',
            onUpdate: () => {
                const value = Number(tweenObj.value) || 0;
                const decimals = this.#amountFractionDigits(value, fractionMode);

                el.textContent = prefix + value.toFixed(decimals) + suffix;
            },
        });
    }

    #addEvents() {
        DOM.addEvent('click', '.share-with-friends', async (event) => {
            event.preventDefault();
            DOM.el('.my_account_hidden_sharer .link.share.qr')?.click();
        });
    }
}
